<template>
  <div class="pc-page theme-page">
    <div class="pc-card" :class="`is-${state}`">
      <!-- Brand -->
      <div class="brand-row">
        <div class="brand-mark" aria-hidden="true">
          <span class="brand-bracket">&#91;</span>
          <span class="brand-glyph"></span>
          <span class="brand-bracket">&#93;</span>
        </div>
        <span class="brand-name">Abol Verify</span>
      </div>

      <div class="pc-status" role="status" aria-live="polite">
        <!-- Verifying -->
        <template v-if="state === 'verifying'">
          <div class="pc-icon pc-icon--neutral">
            <span class="pc-ring"></span>
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <h1>Confirming your payment…</h1>
          <p>Hang tight while we verify your subscription with the payment provider. This only takes a moment.</p>
        </template>

        <!-- Success -->
        <template v-else-if="state === 'success'">
          <div class="pc-icon pc-icon--success pc-pop">
            <i class="pi pi-check"></i>
          </div>
          <h1>Payment successful</h1>
          <p>Your subscription is now active. You're all set — continue to Abol Verify and pick up where you left off.</p>
        </template>

        <!-- Processing / pending -->
        <template v-else-if="state === 'processing'">
          <div class="pc-icon pc-icon--pending">
            <i class="pi pi-clock"></i>
          </div>
          <h1>Payment is processing</h1>
          <p>We haven't received final confirmation from the bank yet. This can take a moment after you pay — we'll keep checking automatically.</p>
        </template>

        <!-- Failed / cancelled -->
        <template v-else-if="state === 'failed'">
          <div class="pc-icon pc-icon--error">
            <i class="pi pi-times"></i>
          </div>
          <h1>Payment not completed</h1>
          <p>{{ errorText }}</p>
        </template>

        <!-- Error (missing reference / could not verify) -->
        <template v-else>
          <div class="pc-icon pc-icon--error">
            <i class="pi pi-exclamation-triangle"></i>
          </div>
          <h1>We couldn't confirm this payment</h1>
          <p>{{ errorText }}</p>
        </template>
      </div>

      <!-- Receipt summary -->
      <div v-if="showReceipt" class="pc-receipt">
        <div v-if="details.planName" class="pc-receipt-row">
          <span class="pc-receipt-key">Plan</span>
          <span class="pc-receipt-val pc-plan">{{ details.planName }}</span>
        </div>
        <div v-if="details.amount != null" class="pc-receipt-row">
          <span class="pc-receipt-key">Amount</span>
          <span class="pc-receipt-val pc-amount">{{ money(details.amount, details.currency) }}</span>
        </div>
        <div v-if="details.invoiceId" class="pc-receipt-row">
          <span class="pc-receipt-key">Invoice</span>
          <span class="pc-receipt-val">#{{ details.invoiceId }}</span>
        </div>
        <div v-if="details.reference" class="pc-receipt-row">
          <span class="pc-receipt-key">Reference</span>
          <span class="pc-receipt-val pc-ref" :title="details.reference">{{ details.reference }}</span>
        </div>
        <div class="pc-receipt-row">
          <span class="pc-receipt-key">Status</span>
          <span class="badge" :class="statusBadge.cls">{{ statusBadge.label }}</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="pc-actions">
        <template v-if="state === 'success'">
          <button type="button" class="pc-btn pc-btn--primary" v-on:click="goContinue">
            <span>{{ isAuthenticated ? "Continue to dashboard" : "Sign in to continue" }}</span>
            <i class="pi pi-arrow-right"></i>
          </button>
          <button v-if="isAuthenticated" type="button" class="pc-btn pc-btn--ghost" v-on:click="goToSubscription">
            View my subscription
          </button>
        </template>

        <template v-else-if="state === 'processing'">
          <button type="button" class="pc-btn pc-btn--primary" :disabled="checking" v-on:click="check">
            <span v-if="checking" class="pc-spinner" aria-hidden="true"></span>
            <span>{{ checking ? "Checking…" : "Check again" }}</span>
          </button>
          <button type="button" class="pc-btn pc-btn--ghost" v-on:click="goContinue">
            {{ isAuthenticated ? "Continue anyway" : "Go to sign in" }}
          </button>
        </template>

        <template v-else-if="state === 'failed'">
          <button type="button" class="pc-btn pc-btn--primary" v-on:click="goToSubscription">
            {{ isAuthenticated ? "Back to plans" : "Sign in to retry" }}
          </button>
          <button v-if="identifier" type="button" class="pc-btn pc-btn--ghost" :disabled="checking" v-on:click="check">
            <span v-if="checking" class="pc-spinner pc-spinner--dark" aria-hidden="true"></span>
            <span>{{ checking ? "Checking…" : "Check again" }}</span>
          </button>
        </template>

        <template v-else>
          <button type="button" class="pc-btn pc-btn--primary" v-on:click="goContinue">
            {{ isAuthenticated ? "Go to dashboard" : "Go to sign in" }}
          </button>
          <button v-if="identifier" type="button" class="pc-btn pc-btn--ghost" :disabled="checking" v-on:click="check">
            <span v-if="checking" class="pc-spinner pc-spinner--dark" aria-hidden="true"></span>
            <span>{{ checking ? "Checking…" : "Try again" }}</span>
          </button>
        </template>
      </div>

      <p class="pc-secure">
        <i class="pi pi-lock"></i>
        <span>Payments are securely processed by Chapa.</span>
      </p>
    </div>

    <span class="text-center copyright pc-footer">
      &copy; Copyright {{ new Date().getFullYear() }} Abol Verify
    </span>
  </div>
</template>

<script>
import { VERIFY_SUBSCRIPTION_PAYMENT } from "@/store/action_types";
import {
  DASHBOARD_ROUTE,
  LOGIN_ROUTE,
  SUBSCRIPTION_ROUTE,
} from "@/router/routes";

const MAX_AUTO_RETRIES = 3;
const RETRY_DELAY_MS = 4000;

export default {
  title: "Payment confirmation",
  data() {
    return {
      // verifying | success | processing | failed | error
      state: "verifying",
      checking: false,
      autoRetries: 0,
      retryTimer: null,
      errorText: "The payment reference is missing or could not be confirmed.",
      details: {
        planName: null,
        amount: null,
        currency: null,
        invoiceId: null,
        reference: null,
        status: null,
        subscriptionId: null,
        paymentId: null,
        txRef: null,
      },
    };
  },
  computed: {
    isAuthenticated() {
      return !!(this.$store.getters.sessionToken && this.$store.getters.account.data);
    },
    // The identifier passed to the backend. Chapa appends tx_ref/trx_ref to the
    // return url; the page also accepts paymentId / subscription(Id) query params.
    identifier() {
      const q = this.$route.query;
      const txRef = q.tx_ref || q.trx_ref || q.txRef || null;
      const paymentId = q.paymentId || q.payment_id || null;
      const subscriptionId = q.subscription || q.subscriptionId || null;
      if (txRef) return { txRef: String(txRef) };
      if (paymentId) return { paymentId: String(paymentId) };
      if (subscriptionId) return { subscriptionId: String(subscriptionId) };
      return null;
    },
    showReceipt() {
      return (
        (this.state === "success" || this.state === "processing") &&
        (this.details.planName != null || this.details.amount != null)
      );
    },
    statusBadge() {
      const status = (this.details.status || "").toLowerCase();
      if (this.state === "success" || status === "success") {
        return { label: "Paid", cls: "badge-success" };
      }
      if (status === "failed") return { label: "Failed", cls: "badge-danger" };
      if (status === "cancelled") return { label: "Cancelled", cls: "badge-danger" };
      return { label: "Processing", cls: "badge-warning" };
    },
  },
  methods: {
    money(value, currency) {
      const num = Number(value);
      if (value == null || isNaN(num)) return "—";
      return this.$filters.formatPrice(num, currency || "ETB");
    },
    clearRetry() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
    },
    applyDetails(result) {
      this.details = {
        planName: result.planName ?? null,
        amount: result.amount ?? null,
        currency: result.currency ?? null,
        invoiceId: result.invoiceId ?? null,
        reference: result.reference ?? result.txRef ?? null,
        status: result.status ?? null,
        subscriptionId: result.subscriptionId ?? null,
        paymentId: result.paymentId ?? null,
        txRef: result.txRef ?? null,
      };
    },
    async check() {
      if (!this.identifier) {
        this.state = "error";
        this.errorText = "The payment reference is missing from this link, so we can't confirm the payment.";
        return;
      }

      this.clearRetry();
      this.checking = true;
      const result = await this.$store.dispatch(VERIFY_SUBSCRIPTION_PAYMENT, this.identifier);
      this.checking = false;

      // Network / server failure (no payload returned).
      if (!result) {
        this.state = "error";
        this.errorText =
          this.$store.getters.errorMsg ||
          "We couldn't confirm this payment right now. If you were charged, it will be reflected shortly.";
        return;
      }

      this.applyDetails(result);

      if (result.isPaid) {
        this.state = "success";
        return;
      }

      const status = (result.status || "").toLowerCase();
      if (status === "failed" || status === "cancelled") {
        this.state = "failed";
        this.errorText =
          status === "cancelled"
            ? "This payment was cancelled. You haven't been charged — you can try again whenever you're ready."
            : "This payment didn't go through. You haven't been charged — please try again or use a different method.";
        return;
      }

      // Verified but not settled yet — the webhook may still be in flight.
      this.state = "processing";
      if (this.autoRetries < MAX_AUTO_RETRIES) {
        this.autoRetries += 1;
        this.retryTimer = setTimeout(() => this.check(), RETRY_DELAY_MS);
      }
    },
    goContinue() {
      this.$router.push({ name: this.isAuthenticated ? DASHBOARD_ROUTE : LOGIN_ROUTE });
    },
    goToSubscription() {
      this.$router.push({ name: this.isAuthenticated ? SUBSCRIPTION_ROUTE : LOGIN_ROUTE });
    },
  },
  mounted() {
    if (!this.identifier) {
      this.state = "error";
      this.errorText = "The payment reference is missing from this link, so we can't confirm the payment.";
      return;
    }
    this.check();
  },
  beforeUnmount() {
    this.clearRetry();
  },
};
</script>

<style scoped>
.pc-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(120% 90% at 50% -10%, rgba(115, 196, 29, 0.10) 0%, rgba(115, 196, 29, 0) 55%),
    var(--ehs-color-canvas, #f6f7f4);
}

.pc-card {
  width: 100%;
  max-width: 460px;
  text-align: center;
  padding: 38px 36px 28px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-xl);
  background: var(--ehs-color-surface);
  box-shadow: var(--ehs-shadow-soft);
  animation: pc-card-in 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes pc-card-in {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Brand */
.brand-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 32px;
  height: 32px;
  border-radius: var(--ehs-radius-sm);
  background: linear-gradient(135deg, var(--ehs-color-primary) 0%, #bcdc44 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  letter-spacing: -0.05em;
  box-shadow: 0 4px 12px rgba(115, 196, 29, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.brand-bracket {
  font-family: var(--ehs-font-mono);
  font-size: 1rem;
  font-weight: 600;
  opacity: 0.9;
  line-height: 1;
}

.brand-glyph {
  display: inline-block;
  width: 5px;
  height: 5px;
  background-color: #ffffff;
  border-radius: 1px;
  margin: 0 1px;
}

.brand-name {
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.035em;
}

/* Status icon */
.pc-icon {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1.7rem;
}

.pc-icon--neutral {
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary);
}

.pc-icon--success {
  background: var(--ehs-color-success-soft);
  color: var(--ehs-color-success);
}

.pc-icon--pending {
  background: var(--ehs-color-warning-soft);
  color: #92400e;
}

.pc-icon--error {
  background: var(--ehs-color-danger-soft);
  color: var(--ehs-color-danger);
}

/* Soft pulsing ring while verifying */
.pc-ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid var(--ehs-color-primary);
  opacity: 0;
  animation: pc-ring 1.6s ease-out infinite;
}

@keyframes pc-ring {
  0% { transform: scale(0.85); opacity: 0.45; }
  100% { transform: scale(1.25); opacity: 0; }
}

/* Success check pop */
.pc-pop {
  animation: pc-pop 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes pc-pop {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); }
}

.pc-card h1 {
  margin: 0 0 8px;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--ehs-color-ink);
}

.pc-card p {
  margin: 0 auto;
  max-width: 366px;
  color: var(--ehs-color-muted);
  font-size: 0.9rem;
  line-height: 1.55;
}

/* Receipt summary */
.pc-receipt {
  margin: 22px 0 4px;
  padding: 6px 16px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  background: var(--ehs-color-canvas);
  text-align: left;
}

.pc-receipt-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 11px 0;
  border-bottom: 1px solid var(--ehs-color-border);
}

.pc-receipt-row:last-child {
  border-bottom: 0;
}

.pc-receipt-key {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--ehs-color-muted);
  flex-shrink: 0;
}

.pc-receipt-val {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
  text-align: right;
  min-width: 0;
}

.pc-plan {
  text-transform: capitalize;
}

.pc-amount {
  font-family: var(--ehs-font-display);
  color: var(--ehs-color-primary-active);
}

.pc-ref {
  font-family: var(--ehs-font-mono);
  font-size: 0.74rem;
  font-weight: 500;
  color: var(--ehs-color-text);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Actions */
.pc-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 24px;
}

.pc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 18px;
  border-radius: var(--ehs-radius-md);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.05s ease;
}

.pc-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.pc-btn--primary {
  background: var(--ehs-color-primary);
  border-color: var(--ehs-color-primary);
  color: #ffffff;
}

.pc-btn--primary:hover:not(:disabled) {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.pc-btn--primary .pi {
  font-size: 0.78rem;
}

.pc-btn--ghost {
  background: var(--ehs-color-surface);
  border-color: var(--ehs-color-border);
  color: var(--ehs-color-ink);
}

.pc-btn--ghost:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  border-color: var(--ehs-color-border-strong);
}

.pc-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.pc-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  animation: pc-spin 0.6s linear infinite;
}

.pc-spinner--dark {
  border-color: rgba(0, 0, 0, 0.18);
  border-top-color: var(--ehs-color-ink);
}

@keyframes pc-spin {
  to { transform: rotate(360deg); }
}

/* Secure note */
.pc-secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 20px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.74rem;
}

.pc-secure .pi {
  font-size: 0.7rem;
}

.pc-footer {
  position: absolute;
  bottom: 20px;
  left: 0;
  width: 100%;
  color: var(--ehs-color-muted);
  font-size: 0.85rem;
}

@media (max-width: 540px) {
  .pc-card {
    padding: 30px 22px 24px;
  }

  .pc-card h1 {
    font-size: 1.25rem;
  }
}
</style>
